import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    /*
     * Grouped-media mobile fix:
     * The selected A/B/C group is rendered first. Eagerly request that group's
     * first images and re-align the slideshow after their dimensions are known.
     */
    this.preloadInitialMediaGroup();
    this.#scheduleSlideshowRefresh(0);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    /*
     * The new grouped gallery can connect before mobile Safari has finalized
     * its slide widths. Re-run preparation and force slide 0 into the exact
     * starting position once the replacement has settled.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        newMediaGallery.preloadInitialMediaGroup?.();
        newMediaGallery.slideshow?.refreshCurrentPosition?.(0);
      });
    });
  };

  /**
   * Eagerly loads the first media group, such as B1/B2/B3.
   * Only the group placed first by the Liquid sorting code is affected.
   */
  preloadInitialMediaGroup() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide[data-media-alt]'));

    if (!slides.length) return;

    const firstAlt = this.#normalizeAlt(slides[0].getAttribute('data-media-alt'));
    const firstGroupKey = this.#getGroupKey(firstAlt);

    /*
     * When no grouped ALT key exists, still prepare the first two slides.
     * When it does exist, prepare up to the first four slides in that group.
     */
    const slidesToPrepare = firstGroupKey
      ? slides
          .filter((slide) => this.#getGroupKey(slide.getAttribute('data-media-alt')) === firstGroupKey)
          .slice(0, 4)
      : slides.slice(0, 2);

    slidesToPrepare.forEach((slide, index) => {
      const image = slide.querySelector('.product-media__image');

      if (!(image instanceof HTMLImageElement)) return;

      image.loading = 'eager';
      image.decoding = 'async';

      if (index < 2) {
        image.setAttribute('fetchpriority', 'high');
      }

      const refreshAfterLoad = () => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          const slideshow = this.slideshow;
          if (!slideshow?.refreshCurrentPosition) return;

          const currentSlides = slideshow.slides;
          const slideIndex = currentSlides?.indexOf(slide) ?? -1;
          const isActive = slide.getAttribute('aria-hidden') === 'false';

          slideshow.refreshCurrentPosition(isActive && slideIndex >= 0 ? slideIndex : slideshow.current);
        });
      };

      if (image.complete) {
        if (typeof image.decode === 'function') {
          image.decode().then(refreshAfterLoad).catch(refreshAfterLoad);
        } else {
          refreshAfterLoad();
        }
      } else {
        image.addEventListener('load', refreshAfterLoad, { once: true });
      }
    });
  }

  /**
   * Schedules more than one alignment pass because iOS Safari may finalize the
   * replacement gallery width one frame later than the custom element setup.
   *
   * @param {number} index - Slide index to align.
   */
  #scheduleSlideshowRefresh(index) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.slideshow?.refreshCurrentPosition?.(index);
      });
    });
  }

  /**
   * @param {string|null} value
   * @returns {string}
   */
  #normalizeAlt(value) {
    return String(value || '')
      .replace(/\s+/g, '')
      .trim()
      .toUpperCase();
  }

  /**
   * @param {string|null} value
   * @returns {string}
   */
  #getGroupKey(value) {
    const normalized = this.#normalizeAlt(value);
    const match = normalized.match(/^([A-Z]+)(\d+)$/);

    return match ? match[1] : '';
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom media selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The event that triggered the selection.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
